import { useEffect, useRef, useState } from 'react'

import type { BrokerAccount, BrokerStatus, LiveQuote, MarketMessage } from '@/lib/types'

/** Délai avant reconnexion, doublé à chaque échec puis plafonné. */
const RECONNECT_INITIAL_MS = 1000
const RECONNECT_MAX_MS = 30_000

interface MarketState {
  connected: boolean
  ready: boolean
  state: BrokerStatus['state']
  error: string | null
  account: BrokerAccount | null
  quotes: Record<string, LiveQuote>
  /** Âge de la dernière cotation reçue par le serveur, en secondes. */
  quoteAge: number | null
}

const INITIAL: MarketState = {
  connected: false,
  ready: false,
  state: 'disconnected',
  error: null,
  account: null,
  quotes: {},
  quoteAge: null,
}

/**
 * Flux de marché en temps réel.
 *
 * Deux comportements dictés par l'usage sur iPhone :
 *
 * - **Reconnexion au retour de veille.** iOS suspend les onglets en arrière-plan
 *   et coupe silencieusement les WebSocket. Sans l'écoute de `visibilitychange`,
 *   l'application reviendrait avec un prix figé sur celui d'il y a deux heures —
 *   et rien ne le signalerait à l'écran.
 * - **Reconnexion à délai croissant**, plafonnée : un réseau mobile qui tombe ne
 *   doit pas déclencher une tentative par seconde jusqu'à vider la batterie.
 */
export function useMarket(symbols: string[]): MarketState {
  const [state, setState] = useState<MarketState>(INITIAL)
  const socket = useRef<WebSocket | null>(null)
  const delay = useRef(RECONNECT_INITIAL_MS)
  const timer = useRef<number | null>(null)
  const watched = useRef<string[]>(symbols)
  watched.current = symbols

  useEffect(() => {
    let closed = false

    function sendWatch() {
      if (socket.current?.readyState === WebSocket.OPEN) {
        socket.current.send(JSON.stringify({ action: 'watch', symbols: watched.current }))
      }
    }

    function connect() {
      if (closed) return

      // Même origine que la page : `wss` en production derrière Caddy, `ws` en
      // développement. Aucune URL à configurer.
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
      const ws = new WebSocket(`${scheme}://${window.location.host}/api/ws/market`)
      socket.current = ws

      ws.onopen = () => {
        delay.current = RECONNECT_INITIAL_MS
        setState((previous) => ({ ...previous, connected: true }))
        sendWatch()
      }

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data as string) as MarketMessage
        setState((previous) => {
          if (message.type === 'quotes') {
            const quotes = { ...previous.quotes }
            for (const quote of message.quotes) quotes[quote.symbol] = quote
            return { ...previous, quotes }
          }
          return {
            ...previous,
            ready: message.ready,
            state: message.state,
            error: message.error,
            account: message.account,
            // Un serveur d'une version antérieure n'envoie pas le champ :
            // `undefined` doit se lire « inconnu », pas « figé ».
            quoteAge: message.quote_age ?? null,
          }
        })
      }

      ws.onclose = () => {
        setState((previous) => ({ ...previous, connected: false }))
        if (closed) return
        timer.current = window.setTimeout(connect, delay.current)
        delay.current = Math.min(delay.current * 2, RECONNECT_MAX_MS)
      }

      // `onerror` précède toujours `onclose` : la reconnexion est gérée là.
      ws.onerror = () => ws.close()
    }

    function onVisible() {
      if (document.visibilityState !== 'visible') return
      // Un WebSocket coupé pendant la veille peut rester en état OPEN côté
      // navigateur : on force la vérification en refermant, ce qui déclenche
      // la reconnexion normale.
      if (socket.current?.readyState !== WebSocket.OPEN) {
        if (timer.current) window.clearTimeout(timer.current)
        delay.current = RECONNECT_INITIAL_MS
        connect()
      } else {
        sendWatch()
      }
    }

    connect()
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      closed = true
      document.removeEventListener('visibilitychange', onVisible)
      if (timer.current) window.clearTimeout(timer.current)
      socket.current?.close()
    }
  }, [])

  // Changement de paire affichée : on redemande l'abonnement.
  useEffect(() => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify({ action: 'watch', symbols }))
    }
  }, [symbols.join(',')])

  return state
}
